import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core';
import { Column } from '@shared/table/column';
import { TableComponent } from '@shared/table/table.component';
import { SelectItem } from 'primeng/api';
import { MandantService } from '@service/mandant.service';
import { TABLE_SERVICE } from '@shared/table/table-service';
import { Context } from '@service/context.service';
import { AbstractContentComponent } from '../abstract-content.component';
import { TimeCreditTableService } from './time-credit-table.service';
import { TimeCredit } from '@entity/time-credit';
import { TimeCreditDetailComponent, weekDays } from './time-credit-detail/time-credit-detail.component';
import { HeaderGroup } from '@shared/table/abstract-table.component';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';

import { AnyValue } from '@shared/table/table.types';

@Component({
  selector: 'app-time-credits',
  templateUrl: './time-credits.component.html',
  styleUrls: ['./time-credits.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TABLE_SERVICE,
      useClass: TimeCreditTableService,
    },
  ],
  imports: [TableComponent, ToolbarButtonDirective],
})
export class TimeCreditsComponent extends AbstractContentComponent implements OnInit {
  private mandantService = inject(MandantService);
  private timeCreditTableService = inject<TimeCreditTableService>(TABLE_SERVICE);

  focusDate = new Date();
  columns: Column[] = [];
  headerGroups: HeaderGroup[][] = [
    [
      { colSpan: 1, title: '', frozen: true, minWidth: 300 },
      { colSpan: 3, title: '', width: 540 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.1'), width: 280 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.2'), width: 280 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.3'), width: 280 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.4'), width: 280 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.5'), width: 280 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.6'), width: 280 },
      { colSpan: 2, title: this.translate('PRIMENG.dayNames.0'), width: 280 },
    ],
  ];
  rowSelection: TimeCredit[] = [];

  @ViewChild(TableComponent) table?: TableComponent;
  private organisationalUnits: SelectItem[] = [];

  constructor() {
    super(Context.Mandant, 'MENU.TIME_CREDIT');
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  focusDateChange(): void {
    setTimeout(() => {
      this.timeCreditTableService.reload();
    }, 0);
  }

  addMissingOrganisations(): void {
    const organisationIds = this?.table?.value.map((v) => v.organisationId) ?? [];
    this.organisationalUnits
      .filter((o) => !organisationIds.includes(o.value))
      .forEach((o) => {
        this.table?.addItem({ organisationId: o.value });
      });
  }

  editRows(): void {
    this.dialogService
      .open(TimeCreditDetailComponent, {
        modal: true,
        closable: true,
        data: this.rowSelection[0],
        header: this.translate('ACTIONS.EDIT'),
        focusOnShow: false,
      })
      ?.onClose.subscribe((data: TimeCredit) => {
        if (data) {
          this.rowSelection.forEach((row) => {
            this.table?.update(data.validFrom, row, 'validFrom');
            this.table?.update(data.validTo, row, 'validTo');
            this.table?.update(data.timeAllowancePerHourInPercent, row, 'timeAllowancePerHourInPercent');
            weekDays.forEach((weekday) => {
              const d: AnyValue = data; // ugly...
              this.table?.update(d[weekday + 'LateShift'], row, weekday + 'LateShift');
              this.table?.update(d[weekday + 'NightShift'], row, weekday + 'NightShift');
            });
          });
        }
        this.markForCheck();
      });
  }

  protected onMandantIdChange(id: number | null): void {
    if (!id) {
      return;
    }

    this.rowSelection = [];

    this.mandantService.getOrganisationalUnits(id).subscribe((data) => {
      this.organisationalUnits = data.map((o) => ({ value: o.id, label: o.bezeichnung }) as SelectItem);
      this.columns = [
        {
          field: 'organisationId',
          headerKey: 'MENU.ORGANISATIONAL_UNITS',
          minWidth: '300px',
          options: this.organisationalUnits,
          editable: true,
        },
        { field: 'validFrom', headerKey: 'GLOBAL.GUELTIG_AB', width: '150px', filterType: 'date', editable: true },
        { field: 'validTo', headerKey: 'GLOBAL.GUELTIG_BIS', width: '150px', filterType: 'date', editable: true },
        {
          field: 'timeAllowancePerHourInPercent',
          headerKey: 'TIME_CREDITS.TIME_CREDIT_PER_HOUR_IN_PERCENT',
          width: '240px',
          editable: true,
          filterType: 'integer',
          fastEdit: true,
        },
      ];

      weekDays.forEach((weekday) => {
        this.columns.push(
          {
            field: weekday + 'LateShift',
            headerKey: 'TIME_CREDITS.LATE_SHIFT',
            width: '140px',
            editable: true,
            filterType: 'number',
            fastEdit: true,
          },
          {
            field: weekday + 'NightShift',
            headerKey: 'TIME_CREDITS.NIGHT_SHIFT',
            width: '140px',
            editable: true,
            filterType: 'number',
            fastEdit: true,
          }
        );
      });

      this.markForCheck();
      this?.timeCreditTableService.reload();
    });
  }
}
